<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { badgeToneClass } from 'shared/helpers/channelCatalog';
import { getChannelSvgUrlExact } from 'dashboard/components-next/icon/channelSvgUrls';
import {
  CATALOG_CARD_GRADIENT_CLASSES,
  CATALOG_CARD_GRADIENT_FALLBACK_CLASS,
} from 'dashboard/helper/channelCatalogGradientClasses';

const props = defineProps({
  catalogKey: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  svgSlug: {
    type: String,
    default: null,
  },
  badge: {
    type: Object,
    default: null,
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  learnMoreHref: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['connect']);

const { t } = useI18n();

const svgUrl = computed(() => getChannelSvgUrlExact(props.svgSlug));

const accentGradientClass = computed(
  () =>
    CATALOG_CARD_GRADIENT_CLASSES[props.catalogKey] ||
    CATALOG_CARD_GRADIENT_FALLBACK_CLASS
);

const showBadge = computed(() => {
  const b = props.badge;
  if (!b || typeof b !== 'object') return false;
  return Boolean(
    (b.text && String(b.text).trim()) ||
      (b.emoji && String(b.emoji).trim()) ||
      (b.icon && String(b.icon).trim())
  );
});

const badgeTone = computed(() => badgeToneClass(props.badge?.tone));

/** Stacked title + description with smaller heading (not float/wrap compact layout). */
const STACKED_SMALL_TITLE_KEYS = new Set(['whatsapp', 'whatsapp_browser']);

/** Float + wrap layout for other long titles only. */
const useCompactTitle = computed(() => {
  if (STACKED_SMALL_TITLE_KEYS.has(props.catalogKey)) return false;
  const text = props.title || '';
  const words = text.split(/\s+/).filter(Boolean);
  return text.length > 22 || words.length >= 4;
});

const titleFontClass = computed(() =>
  STACKED_SMALL_TITLE_KEYS.has(props.catalogKey) ? 'text-lg' : 'text-2xl'
);

const onConnect = () => {
  if (props.disabled || props.isComingSoon) return;
  emit('connect');
};
</script>

<template>
  <div class="relative isolate pt-2">
    <div
      v-if="showBadge"
      class="absolute start-[-6px] top-1.5 z-30 inline-flex max-w-[calc(100%-2rem)] -translate-y-1/2 items-center gap-1 rounded-2xl px-2.5 py-1 text-xs font-semibold leading-snug shadow-none !border-0 !ring-0"
      :class="badgeTone"
    >
      <Icon v-if="badge?.icon" :icon="badge.icon" class="size-4 shrink-0" />
      <span v-else-if="badge?.emoji" class="shrink-0 text-sm leading-none">
        {{ badge.emoji }}
      </span>
      <span v-if="badge?.text" class="min-w-0 truncate leading-snug">{{
        badge.text
      }}</span>
    </div>

    <div
      class="relative flex h-[195px] flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
      :class="{
        'opacity-60': disabled && !isComingSoon,
      }"
    >
      <div
        class="pointer-events-none absolute inset-0 rounded-2xl"
        :class="accentGradientClass"
        aria-hidden="true"
      />

      <div class="relative z-10 flex h-full min-h-0 flex-col p-4">
        <div
          class="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 overflow-hidden"
        >
          <template v-if="useCompactTitle">
            <div
              class="col-start-1 row-start-1 row-span-2 min-h-0 min-w-0 flow-root overflow-hidden"
            >
              <h3
                class="float-start me-2 mb-0 max-w-[min(58%,13rem)] text-start text-lg font-bold leading-snug text-n-slate-12 line-clamp-2 break-words hyphens-auto"
              >
                {{ title }}
              </h3>
              <p
                class="text-start text-xs font-normal leading-relaxed text-n-slate-11 line-clamp-4 break-words"
              >
                {{ description }}
              </p>
            </div>
          </template>
          <template v-else>
            <h3
              class="col-start-1 row-start-1 min-w-0 self-start text-start font-bold leading-snug text-n-slate-12 line-clamp-2 break-words hyphens-auto"
              :class="titleFontClass"
            >
              {{ title }}
            </h3>
            <p
              class="col-start-1 row-start-2 min-h-0 min-w-0 overflow-hidden break-words text-start text-xs font-normal leading-relaxed text-n-slate-11 line-clamp-3"
            >
              {{ description }}
            </p>
          </template>
          <div
            class="col-start-2 row-start-1 row-span-2 flex h-[47px] w-12 shrink-0 items-start justify-center justify-self-end self-start pt-0.5"
          >
            <img
              v-if="svgUrl"
              :src="svgUrl"
              alt=""
              decoding="async"
              draggable="false"
              class="h-[47px] w-12 shrink-0 object-contain"
            />
            <Icon
              v-else-if="icon"
              :icon="icon"
              class="h-[47px] w-12 shrink-0 text-n-slate-10"
            />
          </div>
        </div>

        <div
          class="mt-auto flex shrink-0 items-center justify-between gap-2 border-t border-n-weak/80 pt-3 dark:border-n-strong/60"
        >
          <a
            v-if="learnMoreHref"
            :href="learnMoreHref"
            class="min-w-0 text-start text-xs font-medium text-n-blue-10 underline-offset-2 hover:underline"
            @click.stop
          >
            {{ t('INBOX_MGMT.CHANNEL_CATALOG.LEARN_MORE_MESSAGE_TEMPLATES') }}
          </a>
          <span v-else class="min-w-0 shrink" />
          <Button
            variant="outline"
            color="blue"
            size="sm"
            :disabled="disabled || isComingSoon"
            :label="t('INBOX_MGMT.CHANNEL_CATALOG.CONNECT')"
            @click="onConnect"
          />
        </div>
      </div>

      <div
        v-if="isComingSoon"
        class="absolute inset-0 z-30 flex items-center justify-center rounded-2xl bg-gradient-to-br from-n-surface-1/90 via-n-surface-1/75 to-n-surface-1/95 backdrop-blur-[2px]"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CHANNEL_SELECTOR.COMING_SOON') }}
        </span>
      </div>
    </div>
  </div>
</template>
